import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getSupportUnreadCount } from '@/api/support';

dayjs.extend(relativeTime);

/** What each case status means to the customer (spec §1). */
export const CASE_STATUS = {
  open: { label: 'Open', tone: 'bg-brand-tint text-brand-blueDark ring-brand-secondary/20', hint: "We've got your request — a support agent will reply soon." },
  in_progress: { label: 'In progress', tone: 'bg-brand-tint text-brand-blueDark ring-brand-secondary/20', hint: 'A support agent is working on this.' },
  awaiting_customer: { label: 'Waiting for you', tone: 'bg-warning-tint text-warning ring-warning/20', hint: 'We need a reply from you to continue.' },
  awaiting_organiser: { label: 'With the organiser', tone: 'bg-warning-tint text-warning ring-warning/20', hint: "We've asked the organiser and will update you here." },
  resolved: { label: 'Resolved', tone: 'bg-success-tint text-success ring-success/20', hint: 'This case is resolved. Reply or reopen it if you still need help.' },
  closed: { label: 'Closed', tone: 'bg-surface-sunken text-ink-700 ring-ink-200', hint: 'This case is closed.' },
};

export const caseStatus = (status) =>
  CASE_STATUS[status] || { label: (status || '').replace(/_/g, ' '), tone: 'bg-surface-sunken text-ink-700 ring-ink-200', hint: '' };

export const isClosedStatus = (status) => status === 'resolved' || status === 'closed';

export const REFUND_STATUS = {
  requested: { label: 'Requested — under review', tone: 'text-warning' },
  approved: { label: 'Approved — processing', tone: 'text-brand-blueDark' },
  executed: { label: 'Refund processed', tone: 'text-success' },
  rejected: { label: 'Not approved', tone: 'text-danger' },
  failed: { label: "Couldn't be processed — we'll follow up", tone: 'text-danger' },
};

export const REFUND_TYPE_LABEL = {
  cancel_booking: 'Cancel booking & refund',
  full_refund: 'Full refund',
  partial_refund: 'Partial refund',
};

export const timeAgo = (iso) => (iso ? dayjs(iso).fromNow() : '');
export const dateTime = (iso) => (iso ? dayjs(iso).format('D MMM YYYY, h:mm a') : '');

export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/**
 * Unread support replies for the signed-in customer. Polls gently (support
 * replies are minutes apart, not seconds) and refreshes when the tab regains
 * focus. Resolves to 0 when signed out or the endpoint is unavailable.
 */
export function useSupportUnread(user, intervalMs = 60000) {
  const [count, setCount] = useState(0);
  const refresh = useCallback(async () => {
    if (!user) {
      setCount(0);
      return;
    }
    try {
      setCount(await getSupportUnreadCount());
    } catch {
      // keep the last value; the badge is a hint, not critical
    }
  }, [user]);

  useEffect(() => {
    refresh();
    if (!user) return undefined;
    const t = setInterval(refresh, intervalMs);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener('lk:support-read', onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('lk:support-read', onFocus);
    };
  }, [refresh, user, intervalMs]);

  return count;
}

/** Tell the header badge to refresh after a case was read. */
export const notifySupportRead = () => window.dispatchEvent(new CustomEvent('lk:support-read'));

/** Phone/WhatsApp to call for urgent event-day help. */
export function urgentContact(settings = {}) {
  const raw = settings.support_whatsapp || settings.support_phone || '';
  const digits = String(raw).replace(/[^\d+]/g, '');
  return digits
    ? {
        tel: `tel:${digits}`,
        whatsapp: settings.support_whatsapp ? `https://wa.me/${String(settings.support_whatsapp).replace(/\D/g, '')}` : null,
        display: raw,
      }
    : null;
}
